import { Hospital, House } from 'lucide-react';
import { type ReactNode, type Ref, useEffect, useRef } from 'react';

import { BackLink } from './auth-form-helpers';

interface EditionCardProps {
  icon: ReactNode;
  onClick: () => void;
  ref?: Ref<HTMLButtonElement>;
  subtitle: string;
  title: string;
}

interface EditionPickerProps {
  onBack: () => void;
  onChoose: (edition: 'personal' | 'professional') => void;
}

export function EditionPicker({ onBack, onChoose }: EditionPickerProps) {
  const firstCardRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    firstCardRef.current?.focus();
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <BackLink onClick={onBack} />
      <p className="text-sm text-muted-foreground">What are you here for?</p>
      <div className="grid grid-cols-2 gap-3">
        <EditionCard
          ref={firstCardRef}
          title="Personal"
          subtitle="My family's records"
          icon={<House className="h-6 w-6" />}
          onClick={() => onChoose('personal')}
        />
        <EditionCard
          title="Clinic"
          subtitle="Patient records & EMR"
          icon={<Hospital className="h-6 w-6" />}
          onClick={() => onChoose('professional')}
        />
      </div>
    </div>
  );
}

function EditionCard({ ref, icon, title, onClick, subtitle }: EditionCardProps) {
  return (
    <button
      ref={ref}
      type="button"
      className="flex h-full flex-col items-start gap-2 rounded-lg border bg-background p-4 text-left transition-all hover:border-primary hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      onClick={onClick}>
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">{icon}</div>
      <div className="flex flex-col">
        <span className="text-base font-medium">{title}</span>
        <span className="text-xs text-muted-foreground">{subtitle}</span>
      </div>
    </button>
  );
}
